import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, EventMessage, AuthenticationResult, InteractionType } from '@azure/msal-browser';
import { MSALSpaAuthService } from '../../../services/login-sso/msal-auth.service';
import { BehaviorSubject, Observable, Subject, filter, takeUntil, firstValueFrom, timeout, catchError, of, take } from 'rxjs';
import { BaseSiteService, AuthRedirectService, AuthStorageService } from '@spartacus/core';
import { GlobalService } from '../../../services/global.service';
import { AppConstants } from '../../../constants/app-constant';
import { SmartEditLauncherService } from '@spartacus/smartedit/root';
import { GlobalTrackingService } from '../../../services/global-tracking.service';
import { ActionType } from '../../../models/global-tracking.model';
import { Router } from '@angular/router';


@Component({
  selector: 'app-header-sign-in',
  templateUrl: './header-sign-in.component.html',
  styleUrl: './header-sign-in.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HeaderSignInComponent implements OnInit, OnDestroy {

  private readonly unsubscribe$ = new Subject<void>();
  loader$ = new BehaviorSubject(false);
  login$!: Observable<boolean>;
  requestAccessUrl = AppConstants.routeUrls.requestAccess;
  myProfileUrl = AppConstants.routeUrls.myProfile;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    protected baseSiteService: BaseSiteService,
    protected authRedirectService: AuthRedirectService,
    private authStorageService: AuthStorageService,
    private globalService: GlobalService,
    private msalAuthService: MSALSpaAuthService,
    private smartEditService: SmartEditLauncherService,
    private globalTrackingService: GlobalTrackingService,
    private router: Router,

  ) {
    this.globalService.loginBannerSubject.next(this.globalService.showBanner());
  }

  ngOnInit(): void {
    // CRITICAL: Check for stored redirect response first (before setting up subscriptions)
    // This ensures we don't miss authentication from redirect that happened before component init
    const storedResponse = this.msalAuthService.getAndClearRedirectResponse();
    if (storedResponse) {
      const redirectEvent: EventMessage = {
        eventType: EventType.LOGIN_SUCCESS,
        interactionType: storedResponse.fromCache ? null : InteractionType.Redirect,
        payload: storedResponse,
        error: null,
        timestamp: Date.now()
      };
      this.globalService.checkAndUpdateMsalData(redirectEvent);
    }

    // Subscribe to base site separately to ensure it doesn't block authentication
    this.baseSiteService.getActive().pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe();

    // Subscribe to MSAL events independently to ensure immediate processing
    this.msalBroadcastService.msalSubject$.pipe(
      filter(
        (msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.SSO_SILENT_SUCCESS
      ),
      takeUntil(this.unsubscribe$)
    ).subscribe((result: EventMessage) => {
      this.globalService.checkAndUpdateMsalData(result);
    });

    this.login$ = this.globalService.loginBanner$;
    this.channelListener();
  }

  async login() {
    const usePopup = this.globalService.checkSmartEdit();
    
    try {
      // Check if user is already authenticated with Azure AD
      const accounts = this.msalService.instance.getAllAccounts();
      const isAlreadyAuthenticated = accounts.length > 0;
      
      // Initiate MSAL login
      await this.msalAuthService.safeLogin(usePopup);
      
      // If user was already authenticated, wait for the backend token exchange to complete
      if (isAlreadyAuthenticated) {
        try {
          // Wait for token using reactive approach (no polling)
          await firstValueFrom(
            this.authStorageService.getToken().pipe(
              filter(token => !!(token && token.access_token)),
              take(1),
              timeout(5000), // 5 second timeout for user feedback
              catchError(err => {
                console.warn('[Component] Timeout waiting for backend token:', err.name);
                // Return empty to allow login to proceed
                return of(null);
              })
            )
          );
        } catch (err) {
          console.warn('[Component] Error while waiting for backend token:', err);
        }
      }
      
      this.globalTrackingService.sendTrackingEvent(ActionType.LOGIN_START, {});
      
    } catch (err: any) {
      console.error('[Component] Login failed:', err);
      // Don't prevent the tracking event on error
      this.globalTrackingService.sendTrackingEvent(ActionType.LOGIN_START, {});
    }
  }

  logOut() {
    this.msalAuthService.coreLogout(true);
  }

  channelListener() {
    let index = 0;
    MSALSpaAuthService.channel.addEventListener("message", (event) => {
      if (event?.data === 'TRIGGER_LOGOUT' && this.globalService.isUserLoggedIn() && index === 0) {
        ++index;
        this.msalAuthService.coreLogout();
      }
    });
  }

  requestAccess() {
    this.globalTrackingService.sendTrackingEvent(ActionType.REQUEST_ACCESS_START, {});
    this.router.navigate([this.requestAccessUrl]);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
    this.globalService.clearMessagesOnDestroy();
  }
}
